import { useState, useEffect, useRef, type ReactNode } from 'react';
import { onIdTokenChanged, type User, type Auth } from 'firebase/auth';
import { collection, deleteField, doc, getDoc, getDocs, query, setDoc, serverTimestamp, where, type DocumentReference } from 'firebase/firestore';
import { auth } from '../lib/firebase';
import { AuthContext, type AuthContextType } from './AuthContext';
import { resolveRoleScope, type RoleScope, type UserInfo } from '../services/auth/roleResolver';
import { extractTeamRoleIdsFromUserDocument } from '../services/auth/teamRoleIdsFromUserDocument';
import { mergeSupervisorLofScope } from '../services/auth/mergeSupervisorLofScope';
import { db } from '../lib/firestore';
import { type RoleContext, hasPermission, roleIndicatesSupervisorNurse } from '../lib/roles';
import { normalizeRoleId, normalizeComplianceCategoryId, normalizeTeamRoleId } from '../lib/idNormalization';
import { logAuditEvent } from '../lib/audit';
import type { DataScopeV1 } from '../types/effectiveScope';
import { getFirestorePathDepartmentId, normalizeDepartmentId } from '../utils/normalizeIds';
import { reconcileDepartmentAssignment } from '../lib/reconcileDepartmentAssignment';
import { clearComplianceHubSessionReportingMonth } from '../lib/complianceHubReportingMonthStorage';
import { queryClient } from '../lib/queryClient';
import { clearReportingClientCaches } from '../services/dashboardData';

interface AuthProviderProps {
  children: ReactNode;
}

const normalizeString = (value?: unknown) => {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim();
  return normalized || undefined;
};

const normalizeRoleValue = (role: unknown): string | undefined =>
  normalizeRoleId(typeof role === 'string' ? role : undefined);

const normalizeDepartmentIdValue = (value: unknown): string | undefined => {
  const normalized = normalizeDepartmentId(value);
  return normalized || undefined;
};

const isPermissionDenied = (err: unknown): boolean =>
  typeof err === 'object'
  && err !== null
  && 'code' in err
  && (err as { code?: string }).code === 'permission-denied';

const isOfflineFirestoreError = (err: unknown): boolean => {
  if (typeof err !== 'object' || err === null || !('code' in err)) return false;
  const code = (err as { code?: string }).code;
  return code === 'unavailable' || code === 'failed-precondition';
};

const roleIndicatesHeadWardNurse = (role: string | undefined | null): boolean => {
  if (role == null || typeof role !== 'string') return false;
  const normalized = role.trim().toLowerCase().replace(/[-\s]+/g, '_');
  return normalized === 'hwn' || normalized === 'head_ward_nurse' || normalized.includes('head_ward');
};

const departmentRoleByIdHasSupervisorNurse = (map: Record<string, string> | undefined): boolean => {
  if (!map) return false;
  return Object.values(map).some((v) => roleIndicatesSupervisorNurse(v));
};

const roleRank: Record<string, number> = {
  ADMIN: 4,
  AUDITOR: 3,
  SUPERVISOR: 2,
  SUPERVISOR_NURSE: 2,
  SN_SUPERVISOR: 2,
  USER: 1,
  VISITOR: 0,
};

const mergeLegacyUserData = (
  uidData: Record<string, unknown> | null | undefined,
  legacyData: Record<string, unknown> | null | undefined,
) => {
  const merged: Record<string, unknown> = { ...(legacyData ?? {}) };

  Object.entries(uidData ?? {}).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      merged[key] = value;
    }
  });

  const uidRole = normalizeRoleValue(
    (uidData as { generalRole?: unknown; role?: unknown } | null)?.generalRole
      ?? (uidData as { generalRole?: unknown; role?: unknown } | null)?.role,
  );
  const legacyRole = normalizeRoleValue(
    (legacyData as { generalRole?: unknown; role?: unknown } | null)?.generalRole
      ?? (legacyData as { generalRole?: unknown; role?: unknown } | null)?.role,
  );
  const uidRank = uidRole ? (roleRank[uidRole] ?? -1) : -1;
  const legacyRank = legacyRole ? (roleRank[legacyRole] ?? -1) : -1;
  const shouldUseLegacyRole =
    legacyRole
    && legacyRank >= roleRank.SUPERVISOR
    && (!uidRole || legacyRank > uidRank || uidRole === 'USER');

  if (shouldUseLegacyRole) {
    merged.role = legacyRole;
    merged.generalRole = legacyRole;
    if (import.meta.env.DEV) {
      console.log('[Auth] merged role/generalRole', { uidRole, legacyRole });
    }
  }

  if (uidData?.lofRef == null && (legacyData?.lofRef != null || (legacyData as any)?.supervisedLof != null)) {
    merged.lofRef = (legacyData as any)?.lofRef ?? (legacyData as any)?.supervisedLof;
    if (import.meta.env.DEV) {
      console.log('[Auth] merged lofRef from legacy', { lofRef: merged.lofRef });
    }
  }

  if (uidData?.lofId == null && legacyData?.lofId != null) {
    merged.lofId = legacyData.lofId;
    if (import.meta.env.DEV) {
      console.log('[Auth] merged lofId from legacy', { lofId: legacyData.lofId });
    }
  }

  return merged;
};

const extractDocId = (ref: unknown): string | undefined => {
  if (!ref) return undefined;

  if (typeof ref === 'object' && ref !== null) {
    const maybe = ref as { id?: unknown; path?: unknown };
    const id = normalizeString(maybe.id);
    if (id) return id;

    if (typeof maybe.path === 'string') {
      const fromPath = maybe.path.split('/').pop();
      return normalizeString(fromPath);
    }
  }

  if (typeof ref === 'string') {
    const fromPath = ref.split('/').pop();
    return normalizeString(fromPath);
  }

  return undefined;
};

const deriveDepartmentId = (sourceUserData: Record<string, unknown> | null | undefined) => {
  const deptIdDirect = typeof (sourceUserData as { departmentId?: unknown } | null)?.departmentId === 'string'
    ? normalizeDepartmentIdValue((sourceUserData as { departmentId: string }).departmentId) ?? ''
    : '';

  const deptIdFromRef = normalizeDepartmentIdValue(
    extractDocId((sourceUserData as { departmentRef?: unknown } | null)?.departmentRef),
  );

  const deptIdFromArray = Array.isArray((sourceUserData as { department?: unknown } | null)?.department)
    ? normalizeDepartmentIdValue(extractDocId((sourceUserData as { department: unknown[] }).department[0]))
    : undefined;

  return (deptIdDirect || deptIdFromRef || deptIdFromArray || undefined);
};

const resolveLofDepartmentIds = async (
  firestoreDb: typeof db,
  lofRefLike: unknown,
): Promise<string[]> => {
  const lofId = extractDocId(lofRefLike);
  if (!lofId) return [];

  const isDocRef =
    typeof lofRefLike === 'object'
    && lofRefLike !== null
    && 'id' in (lofRefLike as { id?: unknown })
    && 'path' in (lofRefLike as { path?: unknown });
  const lofRef = isDocRef
    ? (lofRefLike as DocumentReference)
    : doc(firestoreDb, 'lofs', lofId);
  const lofSnap = await getDoc(lofRef);
  if (!lofSnap.exists()) return [];

  const data = lofSnap.data() as {
    departmentRefs?: unknown;
    department?: unknown;
    departments?: unknown;
  } | undefined;
  const rawDepartments = data?.departmentRefs ?? data?.department ?? data?.departments ?? [];
  const departmentRefs = Array.isArray(rawDepartments) ? rawDepartments : [];

  const deduped: string[] = [];
  const seen = new Set<string>();

  departmentRefs.forEach((entry) => {
    // Use Firestore path ID (trim only, preserve casing) so cross-audit queries match stored auditedDepartmentId
    const departmentId = getFirestorePathDepartmentId(extractDocId(entry));
    if (!departmentId || seen.has(departmentId)) return;
    seen.add(departmentId);
    deduped.push(departmentId);
  });

  // Fallback: if LOF doc has no departmentRefs, query departments collection by lofId
  // (aligns with dashboardData.fetchSupervisorDepartmentIdsFromLof). Use case variants
  // because departments may store lofId as "ICU" while lof doc id is "icu".
  if (deduped.length === 0) {
    const departmentsCol = collection(firestoreDb, 'departments');
    const variants = [lofId, lofId.toLowerCase(), lofId.toUpperCase()].filter((v, i, arr) => arr.indexOf(v) === i);
    for (const v of variants) {
      const [byLofId, byLofLegacy] = await Promise.all([
        getDocs(query(departmentsCol, where('lofId', '==', v))),
        getDocs(query(departmentsCol, where('lof', '==', v))),
      ]);
      [byLofId, byLofLegacy].forEach((snap) => {
        snap.docs.forEach((docSnap) => {
          const id = getFirestorePathDepartmentId(docSnap.id);
          if (id && !seen.has(id)) {
            seen.add(id);
            deduped.push(id);
          }
        });
      });
      if (deduped.length > 0) break;
    }
  }

  return deduped;
};

const expandTeamRoles = async (teamRoleIds: string[]) => {
  if (!teamRoleIds.length) {
    return { teamIds: [], teamCategoryIds: [], teamRoles: [] as any[] };
  }

  const teamRoles: any[] = [];
  const teamCategoryIds: string[] = [];
  const categorySeen = new Set<string>();

  await Promise.all(
    teamRoleIds.map(async (id) => {
      const teamDocRef = doc(db, 'team_role', id);
      const teamSnap = await getDoc(teamDocRef);
      if (!teamSnap.exists()) return;

      const data = teamSnap.data() as { name?: unknown; categoryIds?: unknown };
      const teamName = typeof data.name === 'string' ? data.name : undefined;
      const rawCategoryIds = Array.isArray(data.categoryIds) ? data.categoryIds : [];

      rawCategoryIds.forEach((entry) => {
        const rawId = extractDocId(entry);
        const categoryId = rawId ? normalizeComplianceCategoryId(rawId) : undefined;
        if (!categoryId || categorySeen.has(categoryId)) return;
        categorySeen.add(categoryId);
        teamCategoryIds.push(categoryId);
      });

      teamRoles.push({
        teamId: id,
        teamCode: id.toUpperCase(),
        teamName: teamName ?? id,
        role: id.toUpperCase(),
      });
    }),
  );

  return { teamIds: teamRoleIds, teamCategoryIds, teamRoles };
};

const mergeAssignments = (claimValue: unknown, firestoreValue: unknown) => {
  const claimList = Array.isArray(claimValue) ? (claimValue as any[]) : [];
  const firestoreList = Array.isArray(firestoreValue) ? (firestoreValue as any[]) : [];

  if (!firestoreList.length && !claimList.length) return undefined;

  if (!firestoreList.length) return claimList;
  if (!claimList.length) return firestoreList;

  const deduped: any[] = [];
  const seen = new Set<string>();
  [...firestoreList, ...claimList].forEach((entry) => {
    const hasId = typeof entry === 'object' && entry !== null && 'id' in entry;
    const hasTeamId = typeof entry === 'object' && entry !== null && 'teamId' in entry;
    const key = hasId
      ? String((entry as { id?: unknown }).id)
      : hasTeamId
        ? String((entry as { teamId?: unknown }).teamId)
        : String(entry);
    if (seen.has(key)) return;
    seen.add(key);
    deduped.push(entry);
  });

  return deduped;
};

export const buildUserInfo = (
  userId: string,
  claims: Record<string, unknown>,
  firestoreUser: Record<string, unknown> | null,
): UserInfo => {
  const firestoreRole =
    (firestoreUser?.generalRole as string | undefined) || (firestoreUser?.role as string | undefined);

  const rolesFromStore = Array.isArray(firestoreUser?.roles)
    ? (firestoreUser.roles as string[]).map((r) => normalizeRoleValue(r)).filter((r): r is string => !!r)
    : undefined;
  const claimRole = normalizeRoleValue(claims.role as string | undefined);
  const normalizedGeneralRole =
    (rolesFromStore?.length ? rolesFromStore[0] : undefined)
    ?? normalizeRoleValue(firestoreRole)
    ?? claimRole
    ?? 'USER';
  const rolesForScope = rolesFromStore?.length
    ? Array.from(new Set(rolesFromStore))
    : claimRole
      ? [claimRole]
      : undefined;

  // Prefer departmentRoleById and departmentIds (multi-dept) over single departmentId
  const fromRoleById = firestoreUser?.departmentRoleById && typeof firestoreUser.departmentRoleById === 'object' && !Array.isArray(firestoreUser.departmentRoleById)
    ? Object.keys(firestoreUser.departmentRoleById as Record<string, unknown>).map((d) => normalizeDepartmentId(d)).filter(Boolean)
    : [];
  const fromDeptIds = Array.isArray(firestoreUser?.departmentIds)
    ? (firestoreUser?.departmentIds as unknown[]).map((deptId) => normalizeDepartmentId(deptId)).filter(Boolean)
    : [];
  const departmentIds = fromRoleById.length ? fromRoleById : fromDeptIds.length ? fromDeptIds : undefined;
  const departmentIdFromStore = normalizeDepartmentIdValue(firestoreUser?.departmentId);
  const departmentId = (departmentIds?.length ? departmentIds[0] : undefined) ?? departmentIdFromStore;

  const mergeTeamRoleStringFields = (): string[] | undefined => {
    const seen = new Set<string>();
    const out: string[] = [];
    const push = (raw: string) => {
      const n = normalizeTeamRoleId(raw.trim());
      if (!n || seen.has(n)) return;
      seen.add(n);
      out.push(n);
    };
    if (Array.isArray(firestoreUser?.team_roles)) {
      (firestoreUser.team_roles as unknown[]).forEach((r) => {
        if (typeof r === 'string') push(r);
      });
    }
    if (Array.isArray(claims.team_roles)) {
      (claims.team_roles as unknown[]).forEach((r) => {
        if (typeof r === 'string') push(r);
      });
    }
    return out.length ? out : undefined;
  };

  return {
    id: userId,
    generalRole: normalizedGeneralRole,
    roles: rolesForScope,
    positionRole: (claims.positionRole as string) ?? (firestoreUser?.positionRole as string),
    departmentId: departmentId as UserInfo['departmentId'],
    departmentIds: departmentIds as UserInfo['departmentIds'],
    departmentRoleById: firestoreUser?.departmentRoleById as UserInfo['departmentRoleById'],
    team_roles: mergeTeamRoleStringFields(),
    teamRoles: mergeAssignments(claims.teamRoles, firestoreUser?.teamRoles),
    teamCategoryIds: (Array.isArray(firestoreUser?.teamCategoryIds)
      ? (firestoreUser?.teamCategoryIds as string[]).filter(Boolean)
      : undefined) as UserInfo['teamCategoryIds'],
    lofId: firestoreUser?.lofId as UserInfo['lofId'],
    roleId: extractDocId(firestoreUser?.roleId) as UserInfo['roleId'],
  };
};

const isAllowlistedAdmin = (user: User) => {
  const email = user.email?.toLowerCase();
  const uid = user.uid ?? '';

  const allowlistedEmails = (import.meta.env.VITE_ADMIN_EMAILS ?? '')
    .split(',')
    .map((v) => v.trim().toLowerCase())
    .filter(Boolean);

  const allowlistedUids = (import.meta.env.VITE_ADMIN_UIDS ?? '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);

  return allowlistedEmails.includes(email ?? '') || allowlistedUids.includes(uid);
};

export const computeIsAdmin = (user: User, claims: Record<string, unknown>, nextRoleContext: RoleContext) => {
  const adminFromClaims = claims.admin === true;
  const adminFromRoles =
    (nextRoleContext.roles?.includes('ADMIN')) ||
    normalizeRoleValue(nextRoleContext.generalRole) === 'ADMIN';
  const adminFromAllowlist = isAllowlistedAdmin(user);

  return adminFromClaims || adminFromRoles || adminFromAllowlist;
};

const hasPayloadChanged = (
  prev: Record<string, unknown> | null,
  next: Record<string, unknown>,
): boolean => {
  if (!prev) return true;
  const keysToCompare = Object.keys(next).filter(
    (k) => k !== 'lastLoginAt' && k !== 'createdAt',
  );
  return keysToCompare.some((k) => {
    const prevVal = prev[k];
    const nextVal = next[k];
    if (prevVal === nextVal) return false;
    try {
      return JSON.stringify(prevVal) !== JSON.stringify(nextVal);
    } catch {
      return prevVal !== nextVal;
    }
  });
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [authUser, setAuthUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [roleContext, setRoleContext] = useState<RoleContext | null>(null);
  const [roleScope, setRoleScope] = useState<RoleScope | null>(null);
  const [dataScope, setDataScope] = useState<DataScopeV1 | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userDocument, setUserDocument] = useState<Record<string, unknown> | null>(null);
  const userDocumentRef = useRef<Record<string, unknown> | null>(null);
  const didAttemptClaimRefreshRef = useRef<Set<string>>(new Set());

  const forceRefresh = async () => {
    await authUser?.getIdToken(true);
  };

  const canAccess = (
    resource: any,
    action: any,
    options: any,
  ): boolean => {
    if (isAdmin) return true;
    if (!roleContext) return false;
    // Assuming you have a hasPermission function, if not, this needs to be implemented
    const permissionGranted = hasPermission(roleContext, resource, action);
    if (options?.audit) {
      const event: any = {
        actor: {
          uid: authUser?.uid ?? 'unknown',
          role: userRole ?? 'unknown'
        },
        action: action,
        allowed: permissionGranted,
        resource: {
          type: resource,
          id: options?.resourceId ?? 'unknown',
        }
      };
      if (options?.reason) {
        event.metadata = { reason: options.reason };
      }
      logAuditEvent(event);
    }
    return permissionGranted;
  };

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      setLoading(true);

      if (user && user.email) {
        try {
          const tokenResult = await user.getIdTokenResult();
          const claims = tokenResult.claims;

          let firestoreUser: Record<string, unknown> | null = null;
          let lofRef: unknown = null;
          let lofId: string | null = null;
          let lofIdsArray: string[] = [];
          const uidDocRef = doc(db, 'users', user.uid);

          try {
            const uidDoc = await getDoc(uidDocRef);
            const isNewUser = !uidDoc.exists();

            let sourceUserData: Record<string, unknown> | null = uidDoc.exists()
              ? (uidDoc.data() as Record<string, unknown>)
              : null;
            let legacyUserDocId: string | null = null;

            if (!sourceUserData) {
              const emailCandidates = Array.from(
                new Set([user.email, user.email.toLowerCase()].filter(Boolean)),
              );

              for (const emailId of emailCandidates) {
                const emailRef = doc(db, 'users', emailId);
                try {
                  const snap = await getDoc(emailRef);
                  if (snap.exists()) {
                    sourceUserData = snap.data() as Record<string, unknown>;
                    legacyUserDocId = snap.id;
                    break;
                  }
                } catch (err) {
                  if (isPermissionDenied(err)) {
                    // Ignore permission-denied on legacy email lookups to avoid blocking auth.
                    console.warn('Permission denied reading legacy user doc', emailId);
                    continue;
                  }
                  throw err;
                }
              }
            } else if (sourceUserData.legacyUserDocId && typeof sourceUserData.legacyUserDocId === 'string') {
              const legacyDocRef = doc(db, 'users', sourceUserData.legacyUserDocId);
              try {
                const legacyDocSnap = await getDoc(legacyDocRef);
                if (legacyDocSnap.exists()) {
                  const legacyUserData = legacyDocSnap.data();
                  sourceUserData = mergeLegacyUserData(sourceUserData, legacyUserData);
                }
              } catch (err) {
                if (!isPermissionDenied(err)) {
                  throw err;
                }
              }
            }

            let departmentRoleById = sourceUserData?.departmentRoleById as Record<string, string> | undefined;
            let departmentRoles = sourceUserData?.departmentRoles as any[] | undefined;
            let removedDepartmentRoleKeys: string[] = [];

            if (departmentRoleById) {
              const normalizedRoleById: Record<string, string> = {};
              Object.entries(departmentRoleById).forEach(([deptId, role]) => {
                const normalizedId = normalizeDepartmentIdValue(deptId);
                if (!normalizedId) return;
                normalizedRoleById[normalizedId] = role;
              });
              departmentRoleById = Object.keys(normalizedRoleById).length ? normalizedRoleById : undefined;
            }

            if (Array.isArray(departmentRoles)) {
              departmentRoles = departmentRoles
                .map((entry) => {
                  const roleEntry = entry as { id?: unknown; role?: unknown };
                  const normalizedId = normalizeDepartmentIdValue(roleEntry.id);
                  if (!normalizedId) return null;
                  return { ...roleEntry, id: normalizedId };
                })
                .filter(Boolean);
            }

            // Prefer departmentRoleById / departmentIds (multi-dept) over single departmentId
            const deptIdsFromRoleById = departmentRoleById ? Object.keys(departmentRoleById) : [];
            const deptIdsFromStore = Array.isArray((sourceUserData as any)?.departmentIds)
              ? (sourceUserData as any).departmentIds.filter((d: unknown) => typeof d === 'string' && d.trim())
              : [];
            const derivedDeptId =
              (deptIdsFromRoleById[0] ?? deptIdsFromStore[0])
              ?? (sourceUserData?.departmentId as string | undefined)
              ?? extractDocId((sourceUserData as any)?.departmentRef)
              ?? extractDocId(Array.isArray((sourceUserData as any)?.department) ? (sourceUserData as any).department[0] : null);

            let departmentId = normalizeDepartmentIdValue(derivedDeptId);

            if (import.meta.env.DEV) console.log('[Auth] after user doc read');
            const teamRoleIds = extractTeamRoleIdsFromUserDocument(sourceUserData ?? undefined, claims);
            if (import.meta.env.DEV && (teamRoleIds?.length ?? 0) > 0) {
              console.log('[Auth] teamRoleIds extracted', {
                teamRoleIds,
                rawTeamRoles: (sourceUserData as any)?.team_roles ?? (sourceUserData as any)?.teamRoles,
              });
            }
            const { teamIds, teamCategoryIds, teamRoles } = await expandTeamRoles(teamRoleIds);

            // roleId doc id (e.g. "head_ward_nurse" -> HEAD_WARD_NURSE after migration)
            const rawRoleDocId = extractDocId((sourceUserData as { roleId?: unknown } | null)?.roleId);
            const roleDocId = rawRoleDocId ? normalizeRoleId(rawRoleDocId) : undefined;
            let roleDocData: Record<string, unknown> | null = null;
            if (rawRoleDocId || roleDocId) {
              const roleDocRef = doc(db, 'roles', roleDocId ?? rawRoleDocId!);
              let roleDocSnap = await getDoc(roleDocRef);
              if (!roleDocSnap.exists() && rawRoleDocId !== roleDocId) {
                roleDocSnap = await getDoc(doc(db, 'roles', rawRoleDocId));
              }
              roleDocData = roleDocSnap.exists() ? (roleDocSnap.data() as Record<string, unknown>) : null;
            }

            // positionRole fallback (e.g. "HWN" -> "hwn")
            const positionFallback = (() => {
              const pos = normalizeRoleValue((sourceUserData as any)?.positionRole)?.toLowerCase();
              if (!pos) return undefined;
              if (pos === 'hwn') return 'hwn';
              return pos;
            })();

            if (departmentId) {
              // ensure dept-role mapping exists for this department
              const desiredDeptRole = roleDocId ?? positionFallback;

              if (desiredDeptRole) {
                const existing = departmentRoleById?.[departmentId];
                if (!existing) {
                  departmentRoleById = { ...(departmentRoleById ?? {}), [departmentId]: desiredDeptRole };
                }
              }
            }

            const roleFromRoleDoc = normalizeRoleValue(roleDocData?.generalRole as string | undefined);
            const roleFromExisting = normalizeRoleValue(
              (sourceUserData as { role?: string; generalRole?: string } | null)?.role
                ?? (sourceUserData as { role?: string; generalRole?: string } | null)?.generalRole,
            );
            const resolvedRole = roleFromRoleDoc ?? roleFromExisting ?? normalizeRoleValue(roleDocId);
            if (import.meta.env.DEV) console.log('[Auth] role/generalRole:', resolvedRole);
            const resolvedPositionRole =
              normalizeRoleValue(claims.positionRole as string | undefined)
              ?? normalizeRoleValue(roleDocData?.positionRole as string | undefined)
              ?? normalizeRoleValue((sourceUserData as { positionRole?: string } | null)?.positionRole);
            if (import.meta.env.DEV) console.log('[Auth] positionRole:', resolvedPositionRole);

            if (!departmentId) {
                const firstDepartmentRole = Array.isArray(departmentRoles) ? departmentRoles[0] : undefined;
                const firstDepartmentRoleId =
                  firstDepartmentRole &&
                  typeof firstDepartmentRole === 'object' &&
                  typeof (firstDepartmentRole as { id?: unknown }).id === 'string'
                    ? normalizeDepartmentIdValue((firstDepartmentRole as { id: string }).id)
                    : undefined;
                departmentId = deriveDepartmentId(sourceUserData) ?? firstDepartmentRoleId;
            }

            const isHeadWardByRole =
              roleIndicatesHeadWardNurse(resolvedRole) ||
              roleIndicatesHeadWardNurse(resolvedPositionRole) ||
              roleIndicatesHeadWardNurse(roleDocId);
            const hasExplicitDepartmentAssignment =
              (Array.isArray(departmentRoles) && departmentRoles.length > 0) ||
              (departmentRoleById != null && Object.keys(departmentRoleById).length > 0) ||
              (Array.isArray((sourceUserData as { departmentIds?: unknown } | null)?.departmentIds) &&
                ((sourceUserData as { departmentIds?: unknown[] }).departmentIds?.length ?? 0) > 0);
            let departmentsHeadedByUser: string[] = [];
            if (isHeadWardByRole && !hasExplicitDepartmentAssignment) {
              try {
                const hwnDeptSnaps = await getDocs(
                  query(collection(db, 'departments'), where('headWardNurseId', '==', user.uid)),
                );
                departmentsHeadedByUser = hwnDeptSnaps.docs
                  .map((snap) => getFirestorePathDepartmentId(snap.id) || snap.id)
                  .filter(Boolean);
              } catch (err) {
                if (import.meta.env.DEV && !isPermissionDenied(err)) {
                  console.warn('[Auth] headWardNurseId reverse lookup failed', { uid: user.uid, err });
                }
              }
            }

            const rolesField = (sourceUserData?.roles ?? {}) as Record<string, unknown>;
            const previousHeadWardNurseOf = Array.isArray(rolesField.head_ward_nurse_of)
              ? rolesField.head_ward_nurse_of
              : [];
            const reconciledDepartments = reconcileDepartmentAssignment({
              departmentRoles,
              departmentRoleById,
              departmentIds: (sourceUserData as { departmentIds?: unknown } | null)?.departmentIds,
              headWardNurseOf: previousHeadWardNurseOf,
              departmentsHeadedByUser,
            });
            departmentRoleById = reconciledDepartments.departmentRoleById;
            departmentRoles = reconciledDepartments.departmentRoles;
            removedDepartmentRoleKeys = reconciledDepartments.removedDepartmentRoleByIdKeys;
            departmentId = reconciledDepartments.departmentIds[0];
            const departmentIds = reconciledDepartments.departmentIds;
            if (
              sourceUserData?.roles &&
              typeof sourceUserData.roles === 'object' &&
              !Array.isArray(sourceUserData.roles)
            ) {
              sourceUserData = {
                ...(sourceUserData ?? {}),
                roles: {
                  ...rolesField,
                  head_ward_nurse_of: reconciledDepartments.headWardNurseOf,
                },
              };
            }

            const displayName =
              (typeof (sourceUserData as any)?.displayName === 'string' && (sourceUserData as any).displayName.trim())
                ? (sourceUserData as any).displayName.trim()
                : (user.displayName ?? null);

            const rawLofRef =
              (sourceUserData as any)?.lofRef
              ?? (sourceUserData as any)?.lof
              ?? (sourceUserData as any)?.supervisedLof
              ?? (sourceUserData as any)?.lofs
              ?? null;
            // Support lofIds array: use first LOF when no single ref
            const rawLofIds = (sourceUserData as any)?.lofIds;
            lofIdsArray = Array.isArray(rawLofIds)
              ? rawLofIds.filter((id: unknown) => typeof id === 'string' && id.trim()).map((id: string) => id.trim())
              : [];
            // Support lofId string when no ref (e.g. legacy data, migrated docs)
            const rawLofIdStr = typeof (sourceUserData as any)?.lofId === 'string' ? (sourceUserData as any).lofId.trim() : null;
            lofRef =
              rawLofRef ??
              (rawLofIdStr ? doc(db, 'lofs', rawLofIdStr) : null) ??
              (lofIdsArray[0] ? doc(db, 'lofs', lofIdsArray[0]) : null);
            lofId = extractDocId(lofRef) ?? lofIdsArray[0] ?? null;
            // Ensure lofIdsArray includes lofId when we have single ref (for Firestore rules + dashboardData)
            if (lofId && !lofIdsArray.includes(lofId)) {
              lofIdsArray = [lofId, ...lofIdsArray];
            }

            // When supervisor has no lofRef in user doc, derive LOF from their department(s) and persist it
            const isSupervisorByRole =
              roleIndicatesSupervisorNurse(resolvedRole) ||
              roleIndicatesSupervisorNurse(resolvedPositionRole) ||
              departmentRoleByIdHasSupervisorNurse(departmentRoleById);
            if (isSupervisorByRole && !lofRef && (departmentId || departmentIds.length > 0)) {
              const deptIdsToTry = departmentId ? [departmentId, ...departmentIds.filter((id) => id !== departmentId)] : departmentIds;
              for (const deptId of deptIdsToTry) {
                const normalized = getFirestorePathDepartmentId(deptId) || deptId;
                if (!normalized) continue;
                try {
                  const deptSnap = await getDoc(doc(db, 'departments', normalized));
                  if (!deptSnap.exists()) continue;
                  const deptData = deptSnap.data() as { lofId?: string; lof?: unknown; lofRef?: unknown; LOF?: unknown };
                  const lofIdFromDept =
                    (typeof deptData.lofId === 'string' && deptData.lofId.trim()) ||
                    (typeof deptData.lof === 'string' && deptData.lof.trim()) ||
                    extractDocId(deptData.LOF) ||
                    extractDocId(deptData.lofRef) ||
                    extractDocId(deptData.lof) ||
                    null;
                  const lofRefFromDept =
                    (typeof deptData.LOF === 'object' && deptData.LOF) ||
                    (typeof deptData.lofRef === 'object' && deptData.lofRef) ||
                    (typeof deptData.lof === 'object' && deptData.lof) ||
                    null;
                  if (lofIdFromDept) {
                    lofRef = doc(db, 'lofs', lofIdFromDept);
                    lofId = lofIdFromDept;
                    if (!lofIdsArray.includes(lofIdFromDept)) {
                      lofIdsArray = [lofIdFromDept, ...lofIdsArray];
                    }
                    if (import.meta.env.DEV) {
                      console.log('[Auth] derived LOF from department for supervisor', { departmentId: normalized, lofId: lofIdFromDept });
                    }
                    break;
                  }
                  if (lofRefFromDept && typeof (lofRefFromDept as { id?: string }).id === 'string') {
                    lofRef = lofRefFromDept as DocumentReference;
                    lofId = (lofRefFromDept as { id: string }).id;
                    if (!lofIdsArray.includes(lofId)) {
                      lofIdsArray = [lofId, ...lofIdsArray];
                    }
                    if (import.meta.env.DEV) {
                      console.log('[Auth] derived LOF ref from department for supervisor', { departmentId: normalized, lofId });
                    }
                    break;
                  }
                } catch {
                  // ignore
                }
              }
              if (!lofRef && import.meta.env.DEV) {
                console.warn('[Auth] Supervisor: could not derive LOF from department(s). Check that departments have lofId/LOF/lofRef set.', {
                  departmentId,
                  departmentIds: departmentIds.slice(0, 5),
                });
              }
            }
            // When supervisor has no lofRef, try reverse lookup: LOFs where supervisor_nurse points to this user
            if (isSupervisorByRole && !lofRef) {
              try {
                const userRef = doc(db, 'users', user.uid);
                const lofsSnap = await getDocs(query(collection(db, 'lofs'), where('supervisor_nurse', '==', userRef)));
                if (lofsSnap.docs.length > 0) {
                  const allLofIds = Array.from(new Set(lofsSnap.docs.map((d) => d.id)));
                  const firstLof = lofsSnap.docs[0];
                  lofRef = doc(db, 'lofs', firstLof.id);
                  lofId = firstLof.id;
                  // Store ALL lofIds so hasSupervisorLofAccessToDept can match any department's LOF
                  lofIdsArray = lofIdsArray.length > 0
                    ? Array.from(new Set([...lofIdsArray, ...allLofIds]))
                    : allLofIds;
                  if (import.meta.env.DEV) {
                    console.info('[Auth trace] derived LOF from lofs.supervisor_nurse reverse lookup', {
                      uid: user.uid,
                      lofId: firstLof.id,
                      lofCount: lofsSnap.docs.length,
                      lofIdsArray,
                    });
                  }
                } else if (import.meta.env.DEV) {
                  console.info('[Auth trace] supervisor_nurse reverse lookup: no LOFs found where supervisor_nurse == user', { uid: user.uid });
                }
                } catch (err) {
                if (import.meta.env.DEV && !isPermissionDenied(err)) {
                  console.warn('[Auth trace] supervisor_nurse reverse lookup failed', { uid: user.uid, err });
                }
              }
            }
            if (isSupervisorByRole && !lofRef && !departmentId && departmentIds.length === 0 && import.meta.env.DEV) {
              console.warn('[Auth] Supervisor has no lofRef and no departmentId/departmentIds. Assign at least one department to this user.');
            }

            const rolesArray = Array.isArray(sourceUserData?.roles)
              ? (sourceUserData.roles as string[]).map((r) => normalizeRoleValue(r)).filter((r): r is string => !!r)
              : resolvedRole
                ? [resolvedRole]
                : [];
            const canonicalRoles = rolesArray.length ? Array.from(new Set(rolesArray)) : undefined;

            const rolesMapPayload =
              sourceUserData?.roles &&
              typeof sourceUserData.roles === 'object' &&
              !Array.isArray(sourceUserData.roles)
                ? (sourceUserData.roles as Record<string, unknown>)
                : null;

            const departmentRoleByIdWrite: Record<string, unknown> = {
              ...(departmentRoleById ?? {}),
            };
            for (const removedKey of removedDepartmentRoleKeys) {
              departmentRoleByIdWrite[removedKey] = deleteField();
            }

            // Canonical: departmentRoleById. Derived for rules: departmentIds, departmentId (primary dept).
            // Removed ward keys are deleted so a map merge cannot keep the previous ward.
            const canonicalPayload: Record<string, unknown> = {
              uid: user.uid,
              email: user.email,
              lastLoginAt: serverTimestamp(),
              ...(Object.keys(departmentRoleByIdWrite).length ? { departmentRoleById: departmentRoleByIdWrite } : {}),
              ...(Array.isArray(departmentRoles) ? { departmentRoles } : {}),
              ...(rolesMapPayload ? { roles: rolesMapPayload } : canonicalRoles?.length ? { roles: canonicalRoles } : {}),
              ...(resolvedRole ? { role: resolvedRole, generalRole: resolvedRole } : {}),
              ...(resolvedPositionRole ? { positionRole: resolvedPositionRole } : {}),
              ...(departmentId ? { departmentId } : {}),
              ...(departmentIds.length ? {
                departmentIds,
                departmentIdsNormalized: Array.from(new Set(departmentIds.map((id) => id.toLowerCase()))),
              } : {}),
              ...(teamIds.length ? { teamIds } : {}),
              ...(teamRoleIds.length ? { team_roles: teamRoleIds } : {}),
              ...(teamCategoryIds.length ? { teamCategoryIds } : {}),
              ...(teamRoles.length ? { teamRoles } : {}),
              ...(legacyUserDocId ? { legacyUserDocId } : {}),
              ...(displayName ? { displayName } : {}),
              ...(lofRef ? { lofRef, lof: lofRef } : {}),
              ...(lofId ? { lofId } : {}),
              ...(lofIdsArray.length ? {
                lofIds: lofIdsArray,
                lofIdsNormalized: Array.from(new Set(lofIdsArray.map((id) => id.toLowerCase()))),
              } : {}),
              ...(roleDocId ? { roleId: roleDocId } : {}),
            };

            if (isNewUser) {
              canonicalPayload.createdAt = serverTimestamp();
            }

            if (import.meta.env.DEV && departmentIds.length > 0) {
              console.log('[Auth] departmentIdsNormalized written', {
                count: canonicalPayload.departmentIdsNormalized
                  ? (canonicalPayload.departmentIdsNormalized as string[]).length
                  : 0,
              });
            }

            if (hasPayloadChanged(sourceUserData, canonicalPayload)) {
              await setDoc(uidDocRef, canonicalPayload, { merge: true });
            } else if (import.meta.env.DEV) {
              console.log('[Auth] skipping write — no meaningful changes');
            }

            firestoreUser = {
              ...(sourceUserData ?? {}),
              ...canonicalPayload,
              id: user.uid,
            } as Record<string, unknown>;

            setUserDocument(firestoreUser);
            userDocumentRef.current = firestoreUser;
          } catch (error) {
            console.error('Failed to fetch user document', error);
            if (isPermissionDenied(error)) {
              // ignore permission-denied errors
            } else if (isOfflineFirestoreError(error)) {
              // Keep existing user document state while connectivity recovers.
            } else {
              setUserDocument(null);
              userDocumentRef.current = null;
            }
          }

          if (!lofRef && lofIdsArray.length === 0) {
            const claimLofIds = [
              ...(Array.isArray(claims.lofIds) ? claims.lofIds : []),
              claims.lof,
            ]
              .filter((id): id is string => typeof id === 'string' && !!id.trim())
              .map((id) => id.trim());
            const uniqueClaimLofIds = Array.from(new Set(claimLofIds));
            if (uniqueClaimLofIds.length > 0) {
              lofIdsArray = uniqueClaimLofIds;
              lofId = uniqueClaimLofIds[0];
              lofRef = doc(db, 'lofs', uniqueClaimLofIds[0]);
            }
          }

          const userInfo: UserInfo = buildUserInfo(user.uid, claims, firestoreUser);
          const { roleContext: nextRoleContext, scope } = resolveRoleScope(userInfo);
          const roleIdIsSupervisorNurse =
            (nextRoleContext.roleId ?? '')
              .toLowerCase()
              .replace(/-/g, '_')
              .includes('supervisor_nurse');
          const hasSupervisorRole =
            roleIndicatesSupervisorNurse(nextRoleContext.generalRole) ||
            roleIndicatesSupervisorNurse(nextRoleContext.positionRole as string) ||
            roleIdIsSupervisorNurse ||
            roleIndicatesSupervisorNurse(nextRoleContext.departmentRole) ||
            (nextRoleContext.departmentRoles ?? []).some((r) =>
              roleIndicatesSupervisorNurse(
                typeof r === 'string' ? r : String((r as { role?: unknown }).role ?? ''),
              ),
            );

          let supervisorDeptIds: string[] = [];
          if (lofIdsArray.length > 1) {
            // Multi-LOF SN: resolve departments for each LOF and merge
            const allDeptIds = new Set<string>();
            for (const lid of lofIdsArray) {
              const depts = await resolveLofDepartmentIds(db, doc(db, 'lofs', lid));
              depts.forEach((id) => allDeptIds.add(id));
            }
            supervisorDeptIds = Array.from(allDeptIds);
          } else {
            supervisorDeptIds = await resolveLofDepartmentIds(db, lofRef);
          }

          const hasLofRef = lofRef != null || lofIdsArray.length > 0;
          const { finalRoleContext, finalScope, dataScope: derivedDataScope } = mergeSupervisorLofScope({
            nextRoleContext,
            scope,
            hasSupervisorRole,
            hasLofRef,
            supervisorDeptIds,
            lofId,
            lofIdsArray,
          });

          // Rules (canTeamRoleReadCategory / nurseNotesTeamAllowedCategoryGate) read get(/users/{uid}),
          // not in-memory roleResolver output. Persist resolver-derived teamAllowedCategoryIds so NN +
          // nurse_notes matches Firestore (avoids stale doc e.g. ethicSurveys-only list blocking gate).
          const syncTeamAllowed = (finalRoleContext.teamAllowedCategoryIds ?? []).filter(
            (x): x is string => typeof x === 'string' && x.trim(),
          );
          const prevTeamAllowed = Array.isArray(firestoreUser?.teamAllowedCategoryIds)
            ? (firestoreUser.teamAllowedCategoryIds as unknown[]).filter((x): x is string => typeof x === 'string')
            : [];
          const normSorted = (arr: string[]) => [...arr].map((s) => s.trim()).filter(Boolean).sort();
          const teamAllowedChanged =
            JSON.stringify(normSorted(syncTeamAllowed)) !== JSON.stringify(normSorted(prevTeamAllowed));
          if (firestoreUser && syncTeamAllowed.length > 0 && teamAllowedChanged) {
            try {
              await setDoc(uidDocRef, { teamAllowedCategoryIds: syncTeamAllowed }, { merge: true });
              firestoreUser = { ...firestoreUser, teamAllowedCategoryIds: syncTeamAllowed };
              userDocumentRef.current = firestoreUser;
              setUserDocument(firestoreUser);
            } catch (syncErr) {
              if (import.meta.env.DEV) {
                console.warn('[Auth] teamAllowedCategoryIds rules-parity sync failed', syncErr);
              }
            }
          }

          if (import.meta.env.DEV && hasSupervisorRole) {
            const eff = finalRoleContext.lineOfWorkDepartmentIds ?? [];
            console.info('[Auth trace] SUPERVISOR_NURSE scope resolution', {
              uid: user.uid,
              lofRef: lofRef ? (typeof lofRef === 'object' && 'path' in lofRef ? (lofRef as { path: string }).path : String(lofRef)) : null,
              lofId,
              lofIdsArray,
              hasLofRef,
              supervisorDeptIdsFromLof: supervisorDeptIds,
              lineOfWorkDepartmentIdsFromResolver: nextRoleContext.lineOfWorkDepartmentIds ?? [],
              effectiveLineOfWorkDepartmentIds: eff,
              dataScope: derivedDataScope,
            });
          }

          setUserRole(finalRoleContext.generalRole || null);
          setRoleContext(finalRoleContext);
          setRoleScope(finalScope);
          setDataScope(derivedDataScope);
          setIsAdmin(computeIsAdmin(user, claims, finalRoleContext));
          setAuthUser(user);
        } catch (error) {
          console.error('Error resolving user role scope', error);
          setUserRole(null);
          setRoleContext(null);
          setRoleScope(null);
          setDataScope(null);
          setIsAdmin(false);
          setAuthUser(user);
        }
      } else {
        clearComplianceHubSessionReportingMonth();
        queryClient.clear();
        clearReportingClientCaches();
        setAuthUser(null);
        setUserRole(null);
        setRoleContext(null);
        setRoleScope(null);
        setDataScope(null);
        setIsAdmin(false);
        setUserDocument(null);
        userDocumentRef.current = null;
        didAttemptClaimRefreshRef.current.clear();
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const value: AuthContextType = {
    authUser,
    loading,
    auth: auth as Auth,
    userRole,
    roleContext,
    roleScope,
    dataScope,
    isAdmin,
    canAccess,
    forceRefresh,
    firestoreUser: userDocument,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
